import {
  type CaptionStyle,
  type FontLike,
  renderCaptionPaths,
  type TextLayer,
} from "@memeup/design/browser";
import { useEffect, useMemo, useState } from "react";
import { loadMemeFont } from "../lib/fonts.ts";

export interface Artwork {
  url: string;
  width: number;
  height: number;
}

export function useMemeFont() {
  const [font, setFont] = useState<FontLike | null>(null);
  useEffect(() => {
    let alive = true;
    loadMemeFont().then((f) => alive && setFont(f));
    return () => {
      alive = false;
    };
  }, []);
  return font;
}

/**
 * The artwork with captions, as SVG. Caption geometry comes from
 * @memeup/design, the same code that renders the 300 DPI print file, so
 * what you see here is what gets printed.
 */
export function DesignSvgContent({
  artwork,
  layers,
  style,
  font,
}: {
  artwork: Artwork;
  layers: TextLayer[];
  style: CaptionStyle;
  font: FontLike | null;
}) {
  const paths = useMemo(
    () =>
      font
        ? renderCaptionPaths({ layers, style, font, width: artwork.width, height: artwork.height })
        : "",
    [font, layers, style, artwork.width, artwork.height],
  );
  return (
    <>
      <image
        href={artwork.url}
        x={0}
        y={0}
        width={artwork.width}
        height={artwork.height}
        preserveAspectRatio="none"
      />
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: path data generated from numeric layout only; user text never enters the markup */}
      <g dangerouslySetInnerHTML={{ __html: paths }} />
    </>
  );
}

export function DesignPreview(props: {
  artwork: Artwork;
  layers: TextLayer[];
  style: CaptionStyle;
  label: string;
  className?: string;
}) {
  const font = useMemeFont();
  return (
    <svg
      className={props.className}
      viewBox={`0 0 ${props.artwork.width} ${props.artwork.height}`}
      role="img"
      aria-label={props.label}
    >
      <DesignSvgContent
        artwork={props.artwork}
        layers={props.layers}
        style={props.style}
        font={font}
      />
    </svg>
  );
}
